import { router, useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';
import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';

import { fetchStaticPageBySlug, useQuery } from '@fhs/client';
import {
  ActionSheet,
  IconStars,
  MediaQueriesContext,
  Text,
  createMqStyles,
  mobileMediaQueries,
  tokens,
} from '@fhs/ui';
import { RenderCurrentStaticPage } from '@fhs-legacy/frontend/src/pages/static/render-current-static-page';
import { EventTypes } from '@fhs-legacy/frontend/src/state/crm-events';
import {
  CustomEventNames,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { useNetworkContext } from '@fhs-legacy/frontend/src/state/network';

import { useDoublePointsMonday } from '../../hooks/use-double-points-monday';

const OFFER_TERMS_SLUG = 'offer-terms';

function useOfferTermsQuery(enabled: boolean) {
  const { groq } = useNetworkContext().sanityEndpoints;

  return useQuery({
    queryKey: ['staticPage', OFFER_TERMS_SLUG],
    queryFn: ({ signal }) => fetchStaticPageBySlug(groq, OFFER_TERMS_SLUG, signal),
    enabled,
  });
}

export function DoublePointsMondayAlertBanner() {
  const { isDoublePointsMondayOn, promotion } = useDoublePointsMonday();
  const { logRBIEvent } = useCRMEventsContext();
  const mqStyles = useMqStyles();
  const [termsVisible, setTermsVisible] = useState(false);
  const { data: offerTermsPage, isLoading: isTermsLoading } = useOfferTermsQuery(termsVisible);

  useFocusEffect(
    useCallback(() => {
      if (isDoublePointsMondayOn) {
        logRBIEvent({
          name: CustomEventNames.INLINE_ALERT_DISPLAYED,
          type: EventTypes.Other,
          attributes: {
            alertType: 'Double Points Monday',
            promotionId: promotion?._id ?? '',
            promotionName: promotion?.name ?? '',
          },
        });
      }
    }, [isDoublePointsMondayOn, logRBIEvent, promotion])
  );

  const onPressTerms = useCallback(() => {
    logRBIEvent({
      name: CustomEventNames.DOUBLE_POINTS_MONDAY_BANNER_CLICK,
      type: EventTypes.UserContent,
      attributes: {
        promotionId: promotion?._id ?? '',
        promotionName: promotion?.name ?? '',
        action: 'view_terms',
      },
    });
    setTermsVisible(true);
  }, [logRBIEvent, promotion]);

  const onCloseTerms = useCallback(() => {
    setTermsVisible(false);
  }, []);

  const onPressBanner = useCallback(() => {
    logRBIEvent({
      name: CustomEventNames.DOUBLE_POINTS_MONDAY_BANNER_CLICK,
      type: EventTypes.UserContent,
      attributes: {
        promotionId: promotion?._id ?? '',
        promotionName: promotion?.name ?? '',
        action: 'banner_tap',
      },
    });
    router.push('/menu');
  }, [logRBIEvent, promotion]);

  if (!isDoublePointsMondayOn) {
    return null;
  }

  return (
    <>
      <Pressable
        testID="double-points-monday-alert-banner"
        style={mqStyles.container}
        onPress={onPressBanner}
      >
        <View style={styles.iconContainer}>
          <IconStars color={tokens.colors.$white} />
        </View>
        <View style={mqStyles.textContainer}>
          <Text.Ui size="md" weight="semibold">
            It's Double Points Monday!
          </Text.Ui>
          <Text.Ui size="sm">Earn 2X the points on all online orders.</Text.Ui>
          <Pressable onPress={onPressTerms} hitSlop={8}>
            <Text.Ui size="xs" style={styles.termsLink}>
              View Terms & Conditions
            </Text.Ui>
          </Pressable>
        </View>
      </Pressable>
      <ActionSheet
        isVisible={termsVisible}
        onClose={onCloseTerms}
        headerTitle="Offer Terms"
        isScrollableDrawer
      >
        <MediaQueriesContext.Provider value={mobileMediaQueries}>
          {isTermsLoading || !offerTermsPage ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={tokens.colors.$houseRed} />
            </View>
          ) : (
            <RenderCurrentStaticPage currentPage={offerTermsPage} hideFooter />
          )}
        </MediaQueriesContext.Provider>
      </ActionSheet>
    </>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      gap: 16,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: tokens.colors.$blackOpacity10,
      marginTop: 12,
    },
    $gteDesktop: {
      paddingHorizontal: 24,
      paddingVertical: 12,
      gap: 24,
      marginTop: 16,
    },
  },
  textContainer: {
    $base: {
      flex: 1,
      gap: 2,
    },
    $gteDesktop: {
      gap: 0,
    },
  },
});

const styles = StyleSheet.create({
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 100,
    backgroundColor: tokens.colors.$houseRed,
    alignItems: 'center',
    justifyContent: 'center',
  },
  termsLink: {
    color: tokens.colors.$houseRed,
    textDecorationLine: 'underline',
    marginTop: 2,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
